<script setup lang="ts">
import { computed } from "vue";

import type { colorTag } from "@/Modules/App/Models/Styles/Colors";

export interface TcMenuItemDetailProps {
  title?: string;
  titleClass?: string;
  description?: string;
  descriptionOverflow?: boolean;
  descriptionClass?: string;
  descriptionColor?: colorTag;
  isClickable?: boolean;
}

const props = withDefaults(defineProps<TcMenuItemDetailProps>(), {
  titleClass: "",
  descriptionColor: "content-mid",
  descriptionClass: "",
  isClickable: true,
});

const descriptionColor = computed(() => `color-${props.descriptionColor}`);
</script>

<template>
  <section class="tc-menu-item-detail" :class="{ 'has-slot': isClickable }">
    <div :class="['tc-menu-item-detail__data', props.descriptionClass]">
      <p :class="props.titleClass">
        {{ props.title }}
      </p>

      <p
        v-if="description"
        class="tc-menu-item-detail__data__description"
        :class="{
          'tc-menu-item-detail__data__description--overflow': props.descriptionOverflow,
          [descriptionColor]: true,
        }"
      >
        {{ props.description }}
      </p>

      <slot name="menu-item-description" />
    </div>

    <slot />
  </section>
</template>

<style lang="scss" scoped>
.tc-menu-item-detail {
  @include sp.padding(r, 4);
  @include sp.padding(y, 2);
  @include border(1, solid, b);
  @include colors.border(primary, low);

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-left: 0;
  gap: $space-3;

  &.has-slot {
    cursor: pointer;
  }

  &__data {
    flex-grow: 1;

    &__title {
      @include text.general(low, l);
      @include colors.color(content, high);
    }

    &__description {
      @include text.general(low, l);
      @include colors.color(content, mid);
      @include sp.margin(false, 0);

      &.active {
        @include colors.color(semantic, success);
      }

      &.inactive {
        @include colors.color(semantic, error);
      }

      &--overflow {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        max-width: 90%;
      }
    }
  }

  &.is-terapist {
    .tc-menu-item-detail__data__description {
      @include text.general(low, m);
    }
  }
}
</style>
